import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, SIZES, fmt, formatApiError } from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);
const blankItem = () => ({ product_id: "", style_id: "", color: "", sizes: Object.fromEntries(SIZES.map((s) => [s, 0])), unit_price: 0 });

export default function ReturnCreate() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ret, setRet] = useState({
    customer_id: "",
    return_date: todayStr(),
    items: [blankItem()],
    refund_method: "",
    note: "",
  });

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(data));
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, []);

  const updateItem = (i, patch) => setRet((p) => ({ ...p, items: p.items.map((it, ix) => ix === i ? { ...it, ...patch } : it) }));
  const updateSize = (i, s, v) => setRet((p) => ({ ...p, items: p.items.map((it, ix) => ix === i ? { ...it, sizes: { ...it.sizes, [s]: Math.max(0, Number(v) || 0) } } : it) }));
  const addItem = () => setRet((p) => ({ ...p, items: [...p.items, blankItem()] }));
  const removeItem = (i) => setRet((p) => ({ ...p, items: p.items.filter((_, ix) => ix !== i) }));

  const onProductChange = (i, productId) => {
    const prod = products.find((x) => x.id === productId);
    if (!prod) { updateItem(i, { product_id: "", style_id: "", color: "", unit_price: 0 }); return; }
    updateItem(i, { product_id: prod.id, style_id: prod.style_id, color: prod.colors[0]?.color || "", unit_price: prod.selling_price || 0 });
  };

  const lineQty = (it) => SIZES.reduce((a, s) => a + (Number(it.sizes[s]) || 0), 0);
  const lineAmount = (it) => lineQty(it) * (Number(it.unit_price) || 0);
  const totalQty = useMemo(() => ret.items.reduce((a, it) => a + lineQty(it), 0), [ret.items]);
  const totalAmount = useMemo(() => ret.items.reduce((a, it) => a + lineAmount(it), 0), [ret.items]);

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const payload = {
        ...ret,
        amount_returned: totalAmount,
        refund_method: ret.refund_method || "",
        note: (ret.note || "").trim(),
        items: ret.items.map((it) => ({
          product_id: it.product_id,
          style_id: it.style_id,
          color: it.color,
          sizes: it.sizes,
          unit_price: Number(it.unit_price) || 0,
        })),
      };
      const { data } = await api.post("/returns", payload);
      nav(`/returns`);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  const canSubmit = ret.customer_id && ret.items.length > 0 && ret.items.every((it) => it.product_id && it.color) && totalQty > 0;

  return (
    <div data-testid="return-create-page">
      <div className="page-header">
        <div><h1 className="page-title">New Return</h1><p className="page-sub">Add returned stock back to inventory</p></div>
      </div>

      {err && <div className="error-msg" data-testid="return-error" style={{ marginBottom: 14 }}>{err}</div>}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-form">
          <div className="field"><label>Return Date</label><input data-testid="ret-date" className="input" type="date" value={ret.return_date} onChange={(e) => setRet({ ...ret, return_date: e.target.value })}/></div>
          <div className="field"><label>Customer *</label>
            <select data-testid="ret-customer" className="select" value={ret.customer_id} onChange={(e) => setRet({ ...ret, customer_id: e.target.value })}>
              <option value="">Select customer...</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} {c.phone ? `— ${c.phone}` : ""}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card-flush" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Returned Items</h2>
          <button className="btn btn-secondary btn-sm" data-testid="add-return-line-btn" onClick={addItem}><Plus size={14}/> Add Item</button>
        </div>
        <div style={{ padding: 16 }}>
          {ret.items.map((it, i) => {
            const prod = products.find((p) => p.id === it.product_id);
            const qty = lineQty(it);
            const amount = lineAmount(it);
            return (
              <div key={i} className="variant-row" data-testid={`ret-line-${i}`} style={{ background: "white" }}>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 1fr 40px", gap: 10, marginBottom: 10 }}>
                  <div className="field"><label>Product</label>
                    <select data-testid={`ret-line-${i}-product`} className="select" value={it.product_id} onChange={(e) => onProductChange(i, e.target.value)}>
                      <option value="">Select product...</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.style_id} — {p.name}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Color</label>
                    <select data-testid={`ret-line-${i}-color`} className="select" value={it.color} onChange={(e) => updateItem(i, { color: e.target.value })} disabled={!prod}>
                      <option value="">Select...</option>
                      {prod?.colors.map((c) => <option key={c.color} value={c.color}>{c.color}</option>)}
                    </select>
                  </div>
                  <div className="field"><label>Est. Return Price (₹/unit)</label><input data-testid={`ret-line-${i}-price`} className="input num" type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updateItem(i, { unit_price: Number(e.target.value) || 0 })}/></div>
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button className="btn btn-danger btn-sm" data-testid={`remove-ret-line-${i}`} onClick={() => removeItem(i)} disabled={ret.items.length === 1}><Trash2 size={14}/></button>
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: `repeat(${SIZES.length}, 1fr) 90px 120px`, gap: 8, alignItems: "end" }}>
                  {SIZES.map((s) => (
                    <div key={s} className="field">
                      <label>Size {s}</label>
                      <input data-testid={`ret-line-${i}-size-${s}`} className="input num" type="number" min="0" value={it.sizes[s] || 0} onChange={(e) => updateSize(i, s, e.target.value)}/>
                    </div>
                  ))}
                  <div className="field"><label>Qty</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", textAlign: "right" }} className="tabular">{qty}</div></div>
                  <div className="field"><label>Est. Amount</label><div style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", textAlign: "right", fontWeight: 600 }} className="tabular">₹ {fmt(amount)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Refund Details</h2>
        <p style={{ color: "var(--text-muted)", fontSize: 13, marginBottom: 14 }}>Amount to be returned is calculated automatically from the line items above. Leave refund method empty if the customer is exchanging for another style instead of a cash refund.</p>
        <div className="grid-form">
          <div className="field">
            <label>Amount to be Returned (₹)</label>
            <div data-testid="ret-amount" className="tabular" style={{ padding: "9px 12px", border: "1px solid var(--border)", borderRadius: 4, background: "#F8FAFC", fontWeight: 600, fontSize: 15 }}>₹ {fmt(totalAmount)}</div>
          </div>
          <div className="field"><label>Refund Method</label>
            <select data-testid="ret-refund-method" className="select" value={ret.refund_method} onChange={(e) => setRet({ ...ret, refund_method: e.target.value })}>
              <option value="">— None —</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="neft">NEFT</option>
            </select>
          </div>
        </div>
        <div className="field" style={{ marginTop: 14 }}>
          <label>Note (optional)</label>
          <textarea data-testid="ret-note" className="input" rows="2" placeholder="e.g. size didn't fit, exchanged for BK-02" value={ret.note} onChange={(e) => setRet({ ...ret, note: e.target.value })}/>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <button className="btn btn-secondary" onClick={() => nav("/returns")}>Cancel</button>
        <button data-testid="submit-return-btn" className="btn btn-primary" disabled={!canSubmit || busy} onClick={submit}>{busy ? "Saving..." : "Save Return"}</button>
      </div>
    </div>
  );
}
